import type { ReactElement } from 'react';

// Flat pill on/off switch — green = ON ("On" opposite the knob), red = OFF ("Off" opposite).
// Shared by the Services catalog (global service Active toggle) and the Clients list
// (client active⇄suspended). Each caller owns its confirm-before-disable and the mutation.
export function ActiveToggle({
  active,
  busy,
  label,
  onToggle,
}: {
  active: boolean;
  busy: boolean;
  /** The thing being toggled (service or client name) — used only for the a11y label. */
  label: string;
  onToggle: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={active}
      aria-busy={busy}
      aria-label={active ? `Disable ${label}` : `Enable ${label}`}
      disabled={busy}
      onClick={onToggle}
      className={[
        'relative inline-flex h-8 w-16 shrink-0 cursor-pointer items-center rounded-full',
        'transition-colors duration-200 ease-in-out motion-reduce:transition-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-accent)]',
        'disabled:cursor-not-allowed disabled:opacity-60',
        'ring-1 ring-inset',
        active
          ? 'bg-[var(--toggle-on-bg)] ring-[var(--toggle-on-border)]'
          : 'bg-[var(--toggle-off-bg)] ring-[var(--toggle-off-border)]',
      ].join(' ')}
    >
      <span
        className={[
          'pointer-events-none absolute select-none text-[10px] font-bold uppercase tracking-wide',
          active ? 'text-[var(--toggle-on-text)]' : 'text-[var(--toggle-off-text)]',
          active ? 'left-2.5' : 'right-2',
        ].join(' ')}
      >
        {active ? 'On' : 'Off'}
      </span>
      <span
        className={[
          // The knob deliberately stays WHITE in both themes: it rides the green/red track
          // color, not the page surface (the s104 sweep briefly made it dark in dark mode).
          'pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-sm',
          'transition-transform duration-200 ease-in-out motion-reduce:transition-none',
          active ? 'translate-x-9' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  );
}
